import React from 'react';
import { motion } from 'framer-motion';
import { LogIn, LogOut, ClipboardList, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { pageVariants } from './utils';
import { useGuestPortal } from './GuestPortalContext';

export function MainMenu({ setView, activeNotice, noticeAccepted }) {
  const { t, language } = useGuestPortal();
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md"
    >
      {/* Active Notice Banner */}
      {activeNotice && !noticeAccepted && (
        <div className={`mb-4 p-4 rounded-xl border-2 ${activeNotice.notice_type !== 'info' ? 'animate-pulse' : ''} ${
          activeNotice.notice_type === 'critical' ? 'bg-red-900/80 border-red-500' :
          activeNotice.notice_type === 'warning' ? 'bg-amber-900/80 border-amber-500' :
          'bg-blue-900/80 border-blue-500'
        }`}>
          <div className="flex items-center gap-3">
            <svg className="w-8 h-8 text-white flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {activeNotice.notice_type === 'info' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              )}
            </svg>
            <div>
              <p className="text-white font-bold">{activeNotice.title}</p>
              <p className="text-white/80 text-sm">
                {activeNotice.notice_type === 'info' 
                  ? (language === 'es' ? 'Información importante - Tap Check In para continuar' : 'Important info - Tap Check In to continue')
                  : (language === 'es' ? 'Debe aceptar el aviso antes de registrarse' : 'You must accept notice before check-in')
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <Card className="bg-vault-surface border border-vault-border shadow-xl p-8 rounded-2xl" data-testid="main-menu-card">
        <CardHeader className="text-center pb-8">
          <CardTitle className="font-outfit text-3xl font-bold text-vault-text tracking-tight">
            {t.welcomeTitle}
          </CardTitle>
          <p className="text-red-600 font-bold text-lg mt-3">
            {t.railroadCrew}
          </p>
          <p className="text-vault-text-secondary font-manrope mt-2">
            {t.selectOption}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={() => setView("checkin")}
            className="w-full h-14 text-lg flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wide"
            data-testid="checkin-btn"
          >
            <LogIn className="w-5 h-5" />
            {t.checkIn}
          </Button>
          <Button
            onClick={() => setView("checkout")}
            className="w-full h-14 text-lg flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide"
            data-testid="checkout-btn"
          >
            <LogOut className="w-5 h-5" />
            {t.checkOut}
          </Button>
          <div className="pt-4 border-t border-vault-border space-y-3">
            <Button
              onClick={() => setView("signin")}
              className="w-full h-12 flex items-center justify-center gap-3 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              data-testid="signin-sheet-btn"
            >
              <ClipboardList className="w-5 h-5" />
              {t.viewSignInSheet}
            </Button>
            <Button
              onClick={() => setView("help")}
              className="w-full h-12 flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-bold uppercase tracking-wide"
              data-testid="help-btn"
            >
              <HelpCircle className="w-5 h-5" />
              {t.howToUseHelp}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
